import React from "react";

const INDENT_PX = 16;

const isPlainObject = (value) => typeof value === "object" && value !== null && !Array.isArray(value);

const isProbablyUrl = (value) => {
  if (typeof value !== "string") {
    return false;
  }
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const PrimitiveValue = ({ value, depth }) => {
  const baseStyle = depth > 0 ? { marginLeft: depth * INDENT_PX } : undefined;

  return (
    <div
      style={{
        ...baseStyle,
        whiteSpace: typeof value === "string" ? "pre-wrap" : undefined,
      }}
      className="tc-break-word-anywhere-flex"
    >
      {typeof value === "string" ? (
        isProbablyUrl(value) ? (
          <a href={value} target="_blank" rel="noreferrer" className="text-decoration-underline">
            {value}
          </a>
        ) : (
          value
        )
      ) : typeof value === "boolean" ? (
        value ? "true" : "false"
      ) : value ?? "null"}
    </div>
  );
};

const NestedValue = ({ value, depth }) => {
  const indentStyle = depth > 0 ? { marginLeft: depth * INDENT_PX } : undefined;

  if (value === null || value === undefined) {
    return (
      <div style={indentStyle} className="text-muted">
        {value === null ? "null" : "undefined"}
      </div>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div style={indentStyle} className="text-muted">
          []
        </div>
      );
    }

    return (
      <div style={indentStyle} className="d-flex flex-column tc-flex-gap-10px">
        {value.map((item, idx) => (
          <div key={idx} className="d-flex flex-column tc-flex-gap-5px">
            <div className="fw-bold">{`[${idx}]`}</div>
            <NestedValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);

    if (entries.length === 0) {
      return (
        <div style={indentStyle} className="text-muted">
          {"{}"}
        </div>
      );
    }

    return (
      <div style={indentStyle} className="d-flex flex-column tc-flex-gap-10px">
        {entries.map(([childKey, childValue]) => (
          <div key={childKey} className="d-flex flex-column tc-flex-gap-5px">
            <div className="fw-bold">{`${childKey}:`}</div>
            <NestedValue value={childValue} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  return <PrimitiveValue value={value} depth={depth} />;
};

export const QueryHistoryItemLogModalItem = ({ label, value, depth = 0 }) => {
  const wrapperStyle = depth > 0 ? { marginLeft: depth * INDENT_PX } : undefined;
  const nextDepth = label !== undefined && label !== null ? depth + 1 : depth;

  return (
    <div className="d-flex flex-column tc-flex-gap-5px" style={wrapperStyle}>
      {label ? <div className={`fw-bold ${depth === 0 ? "fs-4" : ""}`}>{label}</div> : null}
      <NestedValue value={value} depth={nextDepth} />
    </div>
  );
};

export const hasRenderableContent = (value) => {
  if (value === null || value === undefined) {
    return false;
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasRenderableContent(item));
  }

  if (isPlainObject(value)) {
    return Object.values(value).some((item) => hasRenderableContent(item));
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  return true;
};
